import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux/store";
import {
  setEventApplicationSections,
  setEventApplicationSetting,
  setEventItem
} from "../../redux/Reducers/createPostReducer"
import { isOver18YearsOld } from "../../util/utils";
import { REFUND } from "../../util/CreatePost/utils";
import { ParticipantSettingState } from "../../util/CreatePost/types";
import dayjs from "../../util/dayjs";
import { Dayjs } from "dayjs";
import {
  Box,
  Button,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { colors } from "../../common/colors";
import Separator from "../Separator";
import Radios from "../Radios";
import DateSelectDialog from "../GreenCity/DateSelectDialog";
import { styles as commonStyles } from "./CreatePostStyles";
import { T, TF } from "react-translator-component";

interface Props {
  baseDate: Dayjs,
}

export default function EventApplication({ baseDate }: Props) {
  const dispatch = useAppDispatch();

  const { user } = useSelector((state: any) => state.userAuth);
  const groupInfo = useSelector((state: any) => state.gcGroup);

  const postEvent = useSelector((state: any) => state.createPost.values.event);
  const eventEndDate = postEvent.endDate;
  const acceptApplication = postEvent.acceptApplication;
  const applicationCancelRule = postEvent.applicationCancelRule;

  const applicationSetting = postEvent.applicationSetting;
  const applicationStartDate = applicationSetting.startDate;
  const applicationEndDate = applicationSetting.endDate;
  const applicationSections = applicationSetting.sections;
  const isChangabledApplicationSetting = useSelector((state: any) => state.createPost.values.changabled.applicationSetting);

  const errors = useSelector((state: any) => state.createPost.errors.event);
  const isAdultOwner: boolean = isOver18YearsOld(groupInfo?.owner.dob)

  const initSection = () => {
    return {
      name: "",
      numberOfParticipants: "",
      feeAmount: "",
      free: true,
      _destroy: 0,
      key: crypto.randomUUID(),
    }
  }

  const initFormSection = () => {
    if (applicationSections.length === 0) {
      const sections: ParticipantSettingState[] = [initSection()]
      dispatch(setEventApplicationSections({value: sections}));
      dispatch(setEventItem({key: 'applicationCancelRule', value: REFUND.disabled}))
    }
  }

  const handleInputChangeEvent = (index: number, event: any) => {
    const { name, value } = event.target;
    if (name !== 'name' && Number.isNaN(Number(value))) return

    const sections: ParticipantSettingState[] = Object.assign([], JSON.parse(JSON.stringify(applicationSections)));

    const deleteSections: ParticipantSettingState[] =
      sections.filter((section: ParticipantSettingState) => section._destroy === 1)
    const acceptSections: ParticipantSettingState[] = sections
      .filter((section: ParticipantSettingState) => section._destroy === 0)
      .map((section: ParticipantSettingState, i: number) => {
        const newSection: any = {...section}
        if (i === index) newSection[name] = value
        return newSection
      })

    const newSections = [...acceptSections, ...deleteSections]

    const sectionErrors = errors.applicationSetting.sections.map((error: any, i :number) => {
      const newError = {...error}
      if (i === index) newError[name] = ""
      return newError
    });

    dispatch(setEventApplicationSections({value: newSections, error: sectionErrors}));
  };

  const handleRadioChangeEvent = (index: number, value: boolean) => {
    const sections = applicationSections
      .filter((section: ParticipantSettingState) => section._destroy === 0)
      .map((section: any, i :number) => {
        const newSection = {...section}
        if (i === index) newSection.free = value;
        return newSection
      })
    const sectionErrors = errors.applicationSetting.sections.map((error: any, i :number) => {
      const newError = {...error}
      if (i === index) newError.feeAmount = "";
      return newError
    });

    dispatch(setEventApplicationSections({value: sections, errors: sectionErrors}));
  };

  const handleAddSection = () => {
    const newSection: ParticipantSettingState = initSection()

    const sections: ParticipantSettingState[] = [
      ...applicationSections,
      newSection
    ];
    const sectionErrors = [
      ...errors.applicationSetting.sections,
      {
        name: "",
        numberOfParticipants: "",
        feeAmount: "",
      },
    ];

    dispatch(setEventApplicationSections({value: sections, errors: sectionErrors}));
  };

  const handleDeleteSection = (key: string) => {
    const deleteSection = applicationSections.find((section: ParticipantSettingState) => section.key === key )

    if (!deleteSection) return

    let newSections: ParticipantSettingState[] = Object.assign([], JSON.parse(JSON.stringify(applicationSections)));
    if (deleteSection?.id) {
      newSections = newSections.map((section: ParticipantSettingState) => {
        if (section.key === key) {
          section._destroy = 1
        }
        return section
      })
    } else {
      newSections = newSections.filter((section: ParticipantSettingState) => section.key !== key )
    }

    dispatch(setEventApplicationSections({value: newSections}));
  }

  const handleChangeApplicants = (value: string) => {
    dispatch(setEventItem({key: 'acceptApplication', value: value === "true"}))
    if (value === "true") initFormSection()
  }

  useEffect(() => {
    if (!applicationStartDate) return
    applicationStartDate?.isAfter(applicationEndDate) &&
      dispatch(setEventApplicationSetting({key: 'endDate', value: applicationStartDate}))
  }, [applicationStartDate])

  const createFormSectionList = () => {
    const sections = applicationSections.filter((section: ParticipantSettingState) => section._destroy === 0)

    return (
      <Box sx={{ ...styles.particiateSettingContainer}}>
        {sections.map((section: ParticipantSettingState, index: number) => (
          <Box sx={ styles.particiateSettingBox } key={index}>
            <Box>
              <Typography sx={ styles.settingSection }>
                {`${TF(T("participation_item_number"), index + 1)}${T("required")}`}
              </Typography>
              <TextField
                required
                sx={ styles.participationItemName }
                value={section.name}
                error={errors.applicationSetting.sections[index].name !== ""}
                helperText={errors.applicationSetting.sections[index].name}
                onChange={(e) => handleInputChangeEvent(index, e)}
                name="name"
                placeholder={`${T("participation_placeholder")}`}
                multiline
                rows={1}
                variant="outlined"
                fullWidth
              />
              <Separator sx={{ marginTop: "10px" }} color="#D1D1D1" />

              <Typography sx={ styles.settingSection }>
                {`${T("number_of_participants")}${T("required")}`}
              </Typography>
              <Box sx={ styles.settingInputBox}>
                <TextField
                  required
                  sx={ styles.numOfParticipants }
                  value={Number(section.numberOfParticipants)}
                  error={errors.applicationSetting.sections[index].numberOfParticipants !== ""}
                  onChange={(e) => handleInputChangeEvent(index, e)}
                  name="numberOfParticipants"
                  placeholder="5"
                  inputProps={{ inputMode: 'numeric' }}
                  variant="outlined"
                />
                <div>{T("unit_person")}</div>
              </Box>
              {errors.applicationSetting.sections[index].numberOfParticipants && (
                <Typography sx={ styles.acceptApplicationError }>
                  {errors.applicationSetting.sections[index].numberOfParticipants}
                </Typography>
              )}
              <Separator sx={{ marginTop: "10px" }} color="#D1D1D1" />

              <Typography sx={ styles.settingSection }>
                {T("participation_fee")}
              </Typography>
              <Box sx={ styles.settingInputBox}>
                <FormControl component="fieldset">
                  <RadioGroup
                    name={`radio2-${index}`}
                    value={section.free}
                    onChange={(event) =>
                      handleRadioChangeEvent(
                        index,
                        event.target.value === "true" ? true : false
                      )
                    }
                    row
                  >
                    <FormControlLabel
                      value={true}
                      control={
                        <Radio
                          disabled={section.id !== undefined && !isChangabledApplicationSetting}
                        />
                      }
                      label={T("free")}
                    />
                    {isOver18YearsOld(user.dob) && (
                      <>
                        <FormControlLabel
                          value={false}
                          control={
                            <Radio
                              disabled={section.id !== undefined && !isChangabledApplicationSetting}
                            />
                          }
                          label=""
                        />
                      </>
                    )}
                  </RadioGroup>
                </FormControl>

                {isOver18YearsOld(user.dob) && (
                  <>
                    <TextField
                      required
                      style={styles.inputFeeAmount}
                      value={Number(section.feeAmount)}
                      error={errors.applicationSetting.sections[index].feeAmount !== ""}
                      onChange={(e) => handleInputChangeEvent(index, e)}
                      name="feeAmount"
                      disabled={section.free || (section.id !== undefined && !isChangabledApplicationSetting)}
                      placeholder="1000"
                      inputProps={{ inputMode: 'numeric' }}
                      variant="outlined"
                    />
                    <Typography>{T("unit_yen")}</Typography>
                  </>
                )}
              </Box>
              {errors.applicationSetting.sections[index].feeAmount && (
                <Typography sx={ styles.acceptApplicationError }>
                  {errors.applicationSetting.sections[index].feeAmount}
                </Typography>
              )}
            </Box>
            <Separator sx={{ marginTop: "10px" }} color="#D1D1D1" />
            <Box sx={ styles.buttonAreaParticipateSettings }>
              {index === sections.length - 1 &&
                <Button
                  color="secondary"
                  style={ styles.buttonParticipateSetting }
                  size={"large"}
                  variant="outlined"
                  onClick={handleAddSection}
                >
                  {T("add_participation_item")}
                </Button>
              }
              {index > 0 && (section.id === undefined || !!isChangabledApplicationSetting) &&
                <Button
                  color="error"
                  style={ styles.buttonParticipateSetting }
                  onClick={() => handleDeleteSection(section.key)}
                  size={"large"}
                  variant="outlined"
                >
                  {T("delete_participation_item")}
                </Button>
              }
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Typography sx={commonStyles.typographyText}>
        {T("application_button_settings")}
      </Typography>
      <FormControl component="fieldset">
        <FormLabel component="legend"></FormLabel>

        <Radios
          value={acceptApplication}
          options={[
            {
              value: false,
              label: T("not_set_applcation_button"),
              disabled: !isChangabledApplicationSetting,
              desc: (
                <ul>
                  <li>{T("not_set_applcation_button_description")}</li>
                </ul>
              ),
              style: commonStyles.radioLabel
            },
            {
              value: true,
              label: T("set_applcation_button"),
              disabled: !isChangabledApplicationSetting,
              desc: (
                <ul>
                  <li>{T("set_applcation_button_description_1")}</li>
                  <li>{T("set_applcation_button_description_2")} </li>
                  <li>{T("set_applcation_button_description_3")}</li>
                  <li>{T("set_applcation_button_description_4")}</li>
                </ul>
              ),
              style: commonStyles.radioLabel
            },
          ]}
          rowDirection={false}
          onChange={(value) => handleChangeApplicants(value)}
        />

        {acceptApplication && (
          <>
            <Box style={styles.containerInternal}>
              <Typography sx={styles.conditions}>
                {TF(T("parentheses_with_corner"), T("application_start_date"), T("required"))}
              </Typography>
              <DateSelectDialog
                inputedDate={dayjs(applicationStartDate)}
                minDate={baseDate}
                onChange={(date) => dispatch(setEventApplicationSetting({key: 'startDate', value: date}))}
                errorMessage={errors.applicationSetting.startDate}
              />

              <Typography sx={styles.conditions}>
                {TF(T("parentheses_with_corner"), T("application_end_date"), T("required"))}
              </Typography>
              <DateSelectDialog
                inputedDate={dayjs(applicationEndDate)}
                minDate={dayjs(applicationStartDate)}
                maxDate={dayjs(eventEndDate)}
                onChange={(date) => dispatch(setEventApplicationSetting({key: 'endDate', value: date}))}
                errorMessage={errors.applicationSetting.endDate}
              />
            </Box>

            {createFormSectionList()}

            <Box
              style={styles.containerInternal}
              sx={ styles.applicationCancellDesc }
            >
              <Typography sx={styles.conditions}>
                {T("cancellation_of_application")}
              </Typography>
              <FormControl component="fieldset">
                <Radios
                  value={applicationCancelRule}
                  options={[
                    { value: REFUND.disabled, label: REFUND.disabled, disabled: !isAdultOwner },
                    { value: REFUND.enabled, label: T("can_cancel_and_refund"), disabled: !isAdultOwner },
                  ]}
                  rowDirection={false}
                  onChange={(value) => dispatch(setEventItem({key: 'applicationCancelRule', value: value}))}
                />
              </FormControl>
            </Box>
          </>
        )}
      </FormControl>
    </>
  )
}

const styles = {
  containerInternal: {
    padding: "20px 30px 20px 30px",
    borderRadius: "5px",
    backgroundColor: colors.farm_white
  },

  conditions: {
    paddingTop: "5px",
    textAlign: "left",
    fontSize: "15px",
  },

  particiateSettingContainer: {
    marginTop: "20px"
  },

  particiateSettingBox: {
    padding: "20px 30px 20px 30px",
    borderRadius: "5px",
    backgroundColor: colors.farm_white,
    marginTop: "30px"
  },

  settingSection: {
    paddingTop: "35px",
    textAlign: "left",
    fontSize: "15px",
  },

  settingInputBox: {
    display: "flex",
    alignItems: "end",
  },

  participationItemName: {
    marginTop: "15px",
    backgroundColor: colors.farm_white,
  },

  numOfParticipants: {
    marginTop: "15px",
    backgroundColor: colors.farm_white,
    width: "45%",
  },

  acceptApplicationError: {
    color: colors.farm_red600,
    paddingTop: "10px",
    paddingLeft: "14px",
    fontSize: "0.75rem",
  },

  buttonAreaParticipateSettings: {
    paddingTop: "10px",
    backgroundColor: colors.farm_white
  },

  buttonParticipateSetting: {
    width: "80%",
    alignSelf: "center",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
    margin: "15px auto 0",
    whiteSpace: "nowrap"
  },

  inputFeeAmount: {
    marginTop: "15px",
    background: colors.farm_white,
    marginLeft: "-15px",
    width: "25%",
  },

  applicationCancellDesc: {
    marginTop: "20px",
    marginBottom: "20px"
  },
}
